import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { Text } from "components/Typography/text.styled";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@material-ui/core";
import {
  LocationAutocompleteContainer,
  LocationInput,
  SuggestionContainer,
} from "./locationMap.styled";
import { Color } from "config/colors";
import { FontFamily } from "config/font";
import { GOOGLE_KEY } from "services/baseURLs.config";

type Props = {
  address?: string;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setPostalCode: React.Dispatch<React.SetStateAction<string>>;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  inRegistration?: boolean;
  noMap?: boolean;
};

export const LocationMap: React.FC<Props> = ({
  address,
  setAddress,
  setCity,
  setPostalCode,
  setLocation,
  inRegistration,
  noMap,
}) => {
  const [libraries] = useState<["places"]>(["places"]);
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_KEY,
    libraries: libraries,
  });
  const [companyState] = useCompanyStore();
  const [addressString, setAddressString] = useState<string | null>("");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [validAddress, setValidAddress] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const selectionRequestIdRef = useRef(0);
  const hasUserEditedAddressRef = useRef(false);
  const companyAddress = companyState.company?.address;
  const companyCity = companyState.company?.city;
  const companyLocation = companyState.company?.location;

  useEffect(() => {
    if (!inRegistration) {
      hasUserEditedAddressRef.current = false;
      return;
    }
    if (hasUserEditedAddressRef.current) return;
    setAddressString(address ?? "");
  }, [address, inRegistration]);

  useEffect(() => {
    if (inRegistration) return;

    if (companyLocation) {
      const [nextLat, nextLng] = companyLocation
        .split(",")
        .map((coordinate) => Number(coordinate.trim()));
      setLat(Number.isFinite(nextLat) ? nextLat : null);
      setLng(Number.isFinite(nextLng) ? nextLng : null);
    } else {
      setLat(null);
      setLng(null);
    }

    if (companyAddress) {
      setAddressString(companyCity ? `${companyAddress}, ${companyCity}` : companyAddress);
    } else {
      setAddressString("");
    }
  }, [companyAddress, companyCity, companyLocation, inRegistration]);

  const clearResolvedLocation = (): void => {
    setLat(null);
    setLng(null);
    setAddress("");
    setCity("");
    setPostalCode("");
    setLocation("");
  };

  const markAddressInvalid = (): void => {
    clearResolvedLocation();
    setValidAddress(false);
  };

  const handleAddressChange = (nextAddress: string): void => {
    hasUserEditedAddressRef.current = true;
    selectionRequestIdRef.current += 1;
    setAddressString(nextAddress);
    clearResolvedLocation();
    setValidAddress(true);
  };

  const handleSelect = async (selectedAddress: string): Promise<void> => {
    hasUserEditedAddressRef.current = true;
    selectionRequestIdRef.current += 1;
    const requestId = selectionRequestIdRef.current;

    try {
      const results = await geocodeByAddress(selectedAddress);
      const firstResult = results[0];

      if (selectionRequestIdRef.current !== requestId) return;
      if (!firstResult) {
        markAddressInvalid();
        return;
      }

      const latlng = await getLatLng(firstResult);
      if (selectionRequestIdRef.current !== requestId) return;

      const streetNumber = firstResult.address_components.find((component) =>
        component.types.includes("street_number"),
      );
      const street =
        firstResult.address_components.find((component) =>
          component.types.includes("route"),
        ) ?? firstResult.address_components[0];
      const postalTown =
        firstResult.address_components.find((component) =>
          component.types.includes("postal_town"),
        ) ??
        firstResult.address_components.find((component) =>
          component.types.includes("locality"),
        );
      const postalCode = firstResult.address_components.find((component) =>
        component.types.includes("postal_code"),
      );

      if (
        !Number.isFinite(latlng.lat) ||
        !Number.isFinite(latlng.lng) ||
        !postalTown?.long_name ||
        !postalCode?.long_name
      ) {
        markAddressInvalid();
        return;
      }

      setLat(latlng.lat);
      setLng(latlng.lng);
      setLocation(`${latlng.lat},${latlng.lng}`);
      setValidAddress(true);
      setAddressString(firstResult.formatted_address);
      setAddress(`${street?.long_name ?? ""} ${streetNumber?.long_name ?? ""}`.trim());
      setCity(postalTown.long_name);
      setPostalCode(postalCode.long_name.replace(/\s+/g, ""));
    } catch {
      if (selectionRequestIdRef.current === requestId) {
        markAddressInvalid();
      }
    }
  };

  return isLoaded ? (
    <>
      <PlacesAutocomplete
        value={addressString || ""}
        onChange={handleAddressChange}
        onSelect={handleSelect}
        highlightFirstSuggestion
      >
        {({ getInputProps, suggestions, loading, getSuggestionItemProps }) => (
          <LocationAutocompleteContainer>
            <LocationInput
              {...getInputProps({
                placeholder: t("ProfileCompany.LocationPlaceholder"),
                onFocus: () => setIsFocused(true),
                onBlur: () => setIsFocused(false),
              })}
              $isFocused={isFocused}
              $hasError={!validAddress}
            />
            {!validAddress && (
              <Text color={Color.Destructive}>
                {t("ProfileCompany.InvalidAddress")}
              </Text>
            )}
            <div className="autocomplete-dropdown-container">
              {loading && (
                <div style={{ marginTop: "10px" }}>
                  <CircularProgress />
                </div>
              )}
              {suggestions.map((suggestion, index) => {
                return (
                  <SuggestionContainer
                    {...getSuggestionItemProps(suggestion)}
                    key={index}
                  >
                    <Text
                      color={Color.SeaBlue600}
                      fontFamily={FontFamily.MontserratRegular}
                    >
                      {suggestion.description}
                    </Text>
                  </SuggestionContainer>
                );
              })}
            </div>
          </LocationAutocompleteContainer>
        )}
      </PlacesAutocomplete>
      {!noMap && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "300px",
          }}
          center={{ lat: lat || 59.334591, lng: lng || 18.06324 }}
          zoom={15}
        >
          {lat && lng && (
            <Marker
              title={companyState.company?.companyName || ""}
              position={{ lat: lat, lng: lng }}
            />
          )}
        </GoogleMap>
      )}
    </>
  ) : (
    <>
      <CircularProgress />
    </>
  );
};
